import React from 'react';
import { List, Checkbox, Typography } from 'antd';
import styles from '../../layouts/mainLayout.module.scss';

const { Text } = Typography;

interface NotificationResultItemProps {
  item: {
    id: number;
    notificationText?: string;
    parameters?: Array<{
      key: string;
      value: string;
      target?: string;
    }>;
    isRead: boolean;
    createdAt: string;
  };
  isSelected: boolean;
  onItemSelect: (itemId: number, checked: boolean) => void;
  onEntryNameClick: (target: string, e: React.MouseEvent) => void;
}

const NotificationResultItem: React.FC<NotificationResultItemProps> = ({ 
  item, 
  isSelected, 
  onItemSelect, 
  onEntryNameClick 
}) => {
  const renderNotificationText = (notification: any) => {
    if (!notification.notificationText) return 'Notification';
    
    const parts = [];
    let currentText = notification.notificationText;
    
    if (notification.parameters && Array.isArray(notification.parameters)) {
      notification.parameters.forEach((param: any) => {
        const placeholder = `{{${param.key}}}`;
        if (param.key === 'EntryName' && param.target) {
          const split = currentText.split(placeholder);
          if (split.length > 1) {
            parts.push(split[0]);
            parts.push(
              <span 
                key={param.key} 
                style={{ color: 'var(--app-accent-color)', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => onEntryNameClick(param.target, e)}
              >
                {param.value}
              </span>
            );
            currentText = split.slice(1).join(placeholder);
          }
        } else {
          currentText = currentText.replace(placeholder, param.value || '');
        }
      });
    }
    
    if (currentText) {
      parts.push(currentText);
    }
    
    return parts.length > 0 ? parts : currentText;
  };

  const formatTime = (dateString: string) => {
    // API'den gelen tarih GMT+0 (UTC) formatında
    // Eğer 'Z' yoksa UTC olduğunu belirtmek için ekleyelim
    const utcDateString = dateString.includes('Z') ? dateString : dateString + 'Z';
    
    // UTC tarihi parse et
    const utcDate = new Date(utcDateString);
    const now = new Date();
    
    // Eğer tarih geçersizse
    if (isNaN(utcDate.getTime())) {
      return 'Unknown time';
    }
    
    // Fark hesaplama
    const diffInMinutes = Math.floor((now.getTime() - utcDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hr ago`;
    } else if (diffInMinutes < 10080) { // 7 gün
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      // Tarayıcının yerel saatine göre format
      return utcDate.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  };

  return (
    <List.Item 
      className={`${styles.resultItem} ${!item.isRead ? styles.unread : ''}`}
    >
      <div className={styles.notificationItemContent}>
        {!item.isRead && (
          <Checkbox
            checked={isSelected}
            onChange={(e) => onItemSelect(item.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div className={styles.statusDot} style={{
          backgroundColor: item.isRead ? '#666' : '#ff4d4f'
        }} />
        <div className={styles.itemInfo}>
          <div 
            className={`${styles.itemTitle} ${item.isRead ? styles.itemTitleRead : ''}`}
          >
            {renderNotificationText(item)}
          </div>
        </div>
        <div className={styles.itemMeta}>
          <Text style={{ color: item.isRead ? '#767C7A' : 'var(--app-accent-color)' }}>
            {formatTime(item.createdAt)}
          </Text>
        </div>
      </div>
    </List.Item>
  );
};

export default NotificationResultItem; 